import {
  Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, MenuItem, Paper, TextField, Typography, useTheme,
} from "@material-ui/core";
import OpenInFullIcon from "@material-ui/icons/Fullscreen";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  Bar, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { getToken, requestErrorHandler } from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import { calculateForecast, DAY, forecastChart, historyCutoff } from "./stockForecast";

let nextGraphId = 0;

export default function GraphDialog({ show, setShow, edit, noDialog, height, graphKey, _stockChanges }) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [fetchedRows, setFetchedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [historyDays, setHistoryDays] = useState(90);
  const [horizonDays, setHorizonDays] = useState(90);
  const [showEvents, setShowEvents] = useState(false);
  const [subtractReservations, setSubtractReservations] = useState(false);
  const [leadTimeOverride, setLeadTimeOverride] = useState(null);
  const [bufferDays, setBufferDays] = useState(7);
  const [now, setNow] = useState(Date.now());
  const [graphId] = useState(() => `stock-forecast-${++nextGraphId}`);
  const inputId = (large, name) => `${graphId}-${large ? "expanded" : "inline"}-${name}`;
  const text = (key, values) => t(`stockForecast.${key}`, values);
  const itemId = edit?.StockItem?.id;
  const warehouseId = edit?.Warehouse?.id;
  const supplierLeadTime = edit?.StockItemSupplierDetail?.deliveryTime;
  const leadTime = leadTimeOverride == null ? supplierLeadTime ?? "" : leadTimeOverride;
  const leadTimeKnown = leadTime !== "" && Number.isFinite(Number(leadTime));

  useEffect(() => {
    setLeadTimeOverride(null);
    setExpanded(false);
    setSubtractReservations(false);
    setNow(Date.now());
  }, [graphKey, itemId, warehouseId]);

  useEffect(() => {
    if (_stockChanges !== undefined || !itemId || !warehouseId) return;
    let cancelled = false;
    setFetchedRows([]);
    setLoading(true);
    setError(false);
    fetch(EnvSettings.server + "/get_stock_changes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: getToken(), stock_item_id: itemId, warehouse_id: warehouseId }),
    }).then((response) => {
      if (!response.ok) throw new Error("Unable to load stock history");
      return response.json();
    }).then((result) => {
      if (cancelled) return;
      requestErrorHandler(result, dispatch);
      if (result.error) setError(true);
      else setFetchedRows(result.stock || []);
    }).catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [_stockChanges, itemId, warehouseId, dispatch]);

  const rows = _stockChanges === undefined ? fetchedRows : _stockChanges;
  const forecast = useMemo(() => calculateForecast(rows, {
    now, historyDays, free: edit?.free, total: edit?.total,
    alertQuantity: Number(edit?.alertQuantity || 0), warehouseName: edit?.Warehouse?.name,
    reserved: edit?.reserved, subtractReservations,
  }), [rows, now, historyDays, edit, subtractReservations]);
  const data = useMemo(() => forecastChart(forecast, { now, historyDays, horizonDays }), [forecast, now, historyDays, horizonDays]);
  const chartStart = historyCutoff(forecast.history, now, historyDays);
  const date = (time) => time == null ? "—" : new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", year: "numeric" }).format(time);
  const quantity = (value) => value == null ? "—" : new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(value);
  const unit = edit?.unit || "";
  const reorderTime = leadTimeKnown && forecast.thresholdTime != null
    ? forecast.thresholdTime - (Number(leadTime) + Number(bufferDays || 0)) * DAY : null;
  const today = new Date(now).toDateString();
  const actionDate = (time) => time != null && (time <= now || new Date(time).toDateString() === today) ? text("now") : date(time);

  const chart = (large) => (
    <ResponsiveContainer width="100%" height={large ? 520 : height || 330}>
      <ComposedChart data={data} margin={{ top: 20, right: 20, bottom: 10, left: 0 }}>
        <XAxis dataKey="time" type="number" scale="time" domain={[chartStart, now + horizonDays * DAY]}
          tickFormatter={(time) => new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", ...(now - chartStart > 365 * DAY ? { year: "numeric" } : {}) }).format(time)}
          stroke={theme.palette.text.secondary} minTickGap={large ? 35 : 55} />
        <YAxis stroke={theme.palette.text.secondary} width={65} label={unit ? { value: unit, angle: -90, position: "insideLeft" } : undefined} />
        {large && showEvents && <YAxis yAxisId="events" orientation="right" width={45} />}
        <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
          <Paper style={{ padding: 12 }}>
            <Typography variant="body2">{date(label)}</Typography>
            {payload.filter((entry) => entry.value != null).map((entry) => <Typography variant="body2" key={entry.dataKey} style={{ color: entry.color }}>
              {entry.name}: {quantity(entry.value)} {unit}
            </Typography>)}
            {payload[0].payload.incomingQuantity > 0 && <Typography variant="body2">
              {text("deliveryQuantity", { quantity: quantity(payload[0].payload.incomingQuantity), unit })}
            </Typography>}
          </Paper>
        ) : null} />
        <Legend />
        <Line name={text("available")} dataKey="available" stroke={theme.palette.error.main} dot={false} type="stepAfter" connectNulls={false} isAnimationActive={false} />
        <Line name={text("physical")} dataKey="physical" stroke={theme.palette.primary.main} dot={false} type="stepAfter" connectNulls={false} isAnimationActive={false} />
        {forecast.rate != null && <Line name={text("baseline")} dataKey="baseline" stroke="#b36b00" strokeDasharray="6 4" dot={false} connectNulls={false} isAnimationActive={false} />}
        {forecast.rate != null && forecast.deliveries.length > 0 && <Line name={text("withDeliveries")} dataKey="withDeliveries" stroke="#008577" strokeDasharray="4 3" dot={false} connectNulls={false} isAnimationActive={false} />}
        {forecast.rate != null && forecast.deliveries.length > 0 && <Line name={text("stockAfterDelivery")} dataKey="delivery" stroke="#008577" strokeWidth={0} dot={{ r: 5 }} connectNulls={false} isAnimationActive={false} />}
        {large && showEvents && ["dispatch", "manufacturing", "resupply", "correction", "manual_edit", "lifecycle_state", "device_stock_sync"].map((key, index) => (
          <Bar key={key} yAxisId="events" name={text(key)} dataKey={key} fill={["#d32f2f", "#8e24aa", "#008577", "#1976d2", "#757575", "#546e7a", "#795548"][index]} isAnimationActive={false} />
        ))}
        <ReferenceLine y={0} label={text("empty")} stroke={theme.palette.text.secondary} />
        {Number(edit?.alertQuantity) > 0 && <ReferenceLine y={Number(edit.alertQuantity)} label={text("alertQuantity")} stroke={theme.palette.primary.main} strokeDasharray="3 3" />}
        <ReferenceLine x={now} label={text("today")} stroke={theme.palette.text.secondary} strokeDasharray="3 3" />
      </ComposedChart>
    </ResponsiveContainer>
  );

  const content = (large) => !large ? (
    <div style={{ width: "100%", minWidth: 0 }}>
      {chart(false)}
      <Button size="small" startIcon={<OpenInFullIcon />} onClick={() => { setNow(Date.now()); setExpanded(true); }}>{text("expand")}</Button>
    </div>
  ) : (
    <div style={{ width: "100%", minWidth: 0 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Typography variant="subtitle1">{text("title")}</Typography>
          <Typography variant="body2" color="textSecondary">{text("basis")}</Typography>
          {Number(edit?.reserved) > 0 && <>
            <FormControlLabel control={<Checkbox size="small" checked={subtractReservations} onChange={(event) => setSubtractReservations(event.target.checked)} />}
              label={text("subtractReservations", { quantity: quantity(edit.reserved), unit })} />
            <Typography variant="caption" display="block" color="textSecondary">{text("reservationExplanation")}</Typography>
          </>}
        </Grid>
        <Grid item xs={6} sm={large ? 3 : 6}>
          <Typography variant="caption" color="textSecondary">{text("available")}</Typography>
          <Typography>{quantity(forecast.available)} {unit}</Typography>
        </Grid>
        <Grid item xs={6} sm={large ? 3 : 6}>
          <Typography variant="caption" color="textSecondary">{text("daysRemaining")}</Typography>
          <Typography>{forecast.status === "outOfStock" ? "0" : forecast.rate > 0 && forecast.available != null ? Math.ceil(forecast.available / forecast.rate) : "—"}</Typography>
        </Grid>
        <Grid item xs={6} sm={large ? 3 : 6}>
          <Typography variant="caption" color="textSecondary">{text("zeroDate")}</Typography>
          <Typography>{date(forecast.zeroTime)}</Typography>
        </Grid>
        <Grid item xs={6} sm={large ? 3 : 6}>
          <Typography variant="caption" color="textSecondary">{text("thresholdDate")}</Typography>
          <Typography>{actionDate(forecast.thresholdTime)}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color={forecast.status === "outOfStock" ? "error" : "textSecondary"}>{text(forecast.status)}</Typography>
          {forecast.rate != null && <Typography variant="body2">{text("consumption", { quantity: quantity(forecast.rate), unit })}</Typography>}
          <Typography variant="caption" color="textSecondary">{text("historySummary", { start: date(forecast.start), end: date(now), count: forecast.observations, updated: date(forecast.lastUpdated) })}</Typography>
          {forecast.legacy && <Typography variant="body2" color="textSecondary">{text("legacy")}</Typography>}
          {forecast.lastUpdated != null && now - forecast.lastUpdated > 7 * DAY && <Typography variant="body2" color="textSecondary">{text("stale")}</Typography>}
        </Grid>
        <Grid item xs={6}>
          <TextField id={inputId(large, "history")} select fullWidth size="small" variant="outlined" label={text("historyWindow")} value={historyDays} onChange={(event) => setHistoryDays(event.target.value === "all" ? "all" : Number(event.target.value))}>
            {[30, 90, 180].map((days) => <MenuItem key={days} value={days}>{text("days", { count: days })}</MenuItem>)}
            <MenuItem value="all">{text("allHistory")}</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField id={inputId(large, "horizon")} select fullWidth size="small" variant="outlined" label={text("forecastWindow")} value={horizonDays} onChange={(event) => setHorizonDays(Number(event.target.value))}>
            {[30, 90, 180, 365].map((days) => <MenuItem key={days} value={days}>{text("days", { count: days })}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>
      {chart(large)}
      <FormControlLabel control={<Checkbox size="small" checked={showEvents} onChange={(event) => setShowEvents(event.target.checked)} />} label={text("showEvents")} />
      <Grid container spacing={2} style={{ marginTop: 4 }}>
        <Grid item xs={6}>
          <TextField id={inputId(large, "leadTime")} fullWidth size="small" variant="outlined" type="number" inputProps={{ min: 0, step: 1 }} label={text("leadTime")} value={leadTime}
            onChange={(event) => setLeadTimeOverride(event.target.value === "" ? "" : Math.max(0, Number(event.target.value) || 0))} />
        </Grid>
        <Grid item xs={6}>
          <TextField id={inputId(large, "buffer")} fullWidth size="small" variant="outlined" type="number" inputProps={{ min: 0, step: 1 }} label={text("bufferDays")} value={bufferDays}
            onChange={(event) => setBufferDays(event.target.value === "" ? "" : Math.max(0, Number(event.target.value) || 0))} />
        </Grid>
        <Grid item xs={12}>
          <Typography>{text("reorderBy")}: {actionDate(reorderTime)}</Typography>
          <Typography variant="caption" color="textSecondary">{text(leadTimeKnown ? "reorderExplanation" : "enterLeadTime")}</Typography>
          <Typography variant="caption" display="block" color="textSecondary">{text("scenarioSettings")}</Typography>
        </Grid>
        {forecast.deliveries.length > 0 && <Grid item xs={12}>
          <Typography variant="body2">{text("incomingSummary", { count: forecast.deliveries.length, quantity: quantity(forecast.deliveries.reduce((sum, item) => sum + item.quantity, 0)), unit, date: date(forecast.deliveries[0].time) })}</Typography>
        </Grid>}
        {forecast.overdue > 0 && <Grid item xs={12}><Typography variant="body2" color="error">{text("overdue", { quantity: quantity(forecast.overdue), unit })}</Typography></Grid>}
        <Grid item xs={12}><Typography variant="caption" color="textSecondary">{text("uncertainty")}</Typography></Grid>
      </Grid>
    </div>
  );

  const body = (large) => loading ? <CircularProgress size={24} /> : error ? <Typography color="error">{text("loadError")}</Typography> : content(large);
  const close = () => { setExpanded(false); if (!noDialog && setShow) setShow(false); };
  return <>
    {noDialog && body(false)}
    <Dialog open={noDialog ? expanded : Boolean(show)} onClose={close} fullWidth maxWidth="lg" aria-labelledby={`${graphId}-title`}>
      <DialogTitle id={`${graphId}-title`}>{text("title")} — {edit?.StockItem?.name} / {edit?.Warehouse?.name}</DialogTitle>
      <DialogContent dividers>{body(true)}</DialogContent>
      <DialogActions><Button onClick={close}>{t("close")}</Button></DialogActions>
    </Dialog>
  </>;
}
